import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, Settings, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface Rule {
  id: string;
  company_id: string;
  rule_name: string;
  rule_type: 'obligation' | 'escalation' | 'objection_handling';
  configuration: any;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  company_name?: string;
}

export default function RulesPage() {
  const { theme } = useTheme();
  const [rules, setRules] = useState<Rule[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [formData, setFormData] = useState({
    company_id: '',
    rule_name: '',
    rule_type: 'obligation' as 'obligation' | 'escalation' | 'objection_handling',
    configuration: {
      phases: [
        {
          phase: 'soft',
          prompt: '',
          retries: 1
        }
      ],
      escalate_if: ['no_response_after_retries'],
      transfer_summary: true
    },
    priority: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      // Load rules with company info
      const { data: rulesData, error: rulesError } = await supabase
        .from('ai_agent_rules')
        .select(`
          *,
          company_configs(name)
        `)
        .order('created_at', { ascending: false });

      if (rulesError) throw rulesError;

      // Load companies for the dropdown
      const { data: comps, error: compsError } = await supabase
        .from('company_configs')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (compsError) throw compsError;

      setRules(rulesData || []);
      setCompanies(comps || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function saveRule() {
    try {
      const ruleData = {
        company_id: formData.company_id,
        rule_name: formData.rule_name,
        rule_type: formData.rule_type,
        configuration: formData.configuration,
        priority: formData.priority
      };

      if (editingRule) {
        const { error } = await supabase
          .from('ai_agent_rules')
          .update(ruleData)
          .eq('id', editingRule.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('ai_agent_rules')
          .insert(ruleData);

        if (error) throw error;
      }

      await loadData();
      setShowAddForm(false);
      setEditingRule(null);
      resetForm();
    } catch (error) {
      console.error('Error saving rule:', error);
    }
  }

  async function deleteRule(id: string) {
    if (!confirm('Are you sure you want to delete this rule?')) return;

    try {
      const { error } = await supabase
        .from('ai_agent_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadData();
    } catch (error) {
      console.error('Error deleting rule:', error);
    }
  }

  function resetForm() {
    setFormData({
      company_id: '',
      rule_name: '',
      rule_type: 'obligation',
      configuration: {
        phases: [
          {
            phase: 'soft',
            prompt: '',
            retries: 1
          }
        ],
        escalate_if: ['no_response_after_retries'],
        transfer_summary: true
      },
      priority: 0
    });
  }

  function startEdit(rule: Rule) {
    setEditingRule(rule);
    setFormData({
      company_id: rule.company_id,
      rule_name: rule.rule_name,
      rule_type: rule.rule_type,
      configuration: rule.configuration,
      priority: rule.priority
    });
    setShowAddForm(true);
  }

  function addPhase() {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        phases: [
          ...prev.configuration.phases,
          {
            phase: 'firm',
            prompt: '',
            retries: 2
          }
        ]
      }
    }));
  }

  function updatePhase(index: number, field: string, value: any) {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        phases: prev.configuration.phases.map((phase: any, i: number) =>
          i === index ? { ...phase, [field]: value } : phase
        )
      }
    }));
  }

  function removePhase(index: number) {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        phases: prev.configuration.phases.filter((_: any, i: number) => i !== index)
      }
    }));
  }

  function getRuleTypeIcon(type: string) {
    switch (type) {
      case 'obligation':
        return <CheckCircle className="w-4 h-4" style={{ color: theme.colors.primaryColor }} />;
      case 'escalation':
        return <AlertTriangle className="w-4 h-4" style={{ color: theme.colors.warningText }} />;
      case 'objection_handling':
        return <Settings className="w-4 h-4" style={{ color: '#9333ea' }} />;
      default:
        return <Settings className="w-4 h-4" style={{ color: theme.colors.textSecondary }} />;
    }
  }

  function getRuleTypeStyle(type: string) {
    switch (type) {
      case 'obligation':
        return {
          backgroundColor: theme.colors.infoBg,
          borderColor: theme.colors.infoBorder
        };
      case 'escalation':
        return {
          backgroundColor: theme.colors.warningBg,
          borderColor: theme.colors.warningBorder
        };
      case 'objection_handling':
        return {
          backgroundColor: 'rgba(147, 51, 234, 0.1)',
          borderColor: 'rgba(147, 51, 234, 0.3)'
        };
      default:
        return {
          backgroundColor: theme.colors.bgCard,
          borderColor: theme.colors.borderColor
        };
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div 
          className="animate-spin rounded-full h-8 w-8 border-b-2"
          style={{ borderColor: theme.colors.primaryColor }}
        />
      </div>
    );
  }

  return (
    <div className="p-6" style={{ backgroundColor: theme.colors.bgPage }}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
            AI Agent Rules
          </h1>
          <p style={{ color: theme.colors.textSecondary }}>
            Configure multi-step rules and obligations for AI voice agents
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 rounded-lg flex items-center space-x-2"
          style={{
            backgroundColor: theme.colors.primaryColor,
            color: '#ffffff'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
        >
          <Plus className="w-4 h-4" />
          <span>Add Rule</span>
        </button>
      </div>

      <div
        className="mb-6 p-4 rounded-lg"
        style={{
          backgroundColor: theme.colors.bgCard,
          border: `1px solid ${theme.colors.borderColor}`
        }}
      >
        <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
          About this page: Unified rules engine backed by the <span className="font-mono">ai_agent_rules</span> table.
          Define multi-step obligations, escalation policies, and objection handling per company here.
          The legacy per-company rules UI is available on the{' '}
          <a href="/ai-voice-agents/agent-rules" className="underline font-medium">Agent Rules</a> page and should be used only for migrating older records.
        </p>
      </div>

      {/* Rules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rules.map((rule) => (
          <div 
            key={rule.id} 
            className="rounded-lg p-6"
            style={{
              ...getRuleTypeStyle(rule.rule_type),
              border: `1px solid ${getRuleTypeStyle(rule.rule_type).borderColor}`
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-2">
                {getRuleTypeIcon(rule.rule_type)}
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>
                    {rule.rule_name}
                  </h3>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                    {rule.company_name}
                  </p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => startEdit(rule)}
                  style={{ color: theme.colors.textTertiary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteRule(rule.id)}
                  style={{ color: theme.colors.textTertiary }}
                  onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.dangerText}
                  onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textTertiary}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  Type
                </span>
                <span className="text-sm capitalize" style={{ color: theme.colors.textPrimary }}>
                  {rule.rule_type.replace('_', ' ')}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  Priority
                </span>
                <span className="text-sm" style={{ color: theme.colors.textPrimary }}>
                  {rule.priority}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>
                  Phases
                </span>
                <span className="text-sm" style={{ color: theme.colors.textPrimary }}>
                  {rule.configuration.phases?.length || 0}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span 
                  className="inline-flex px-2 py-1 text-xs rounded-full"
                  style={rule.is_active ? {
                    backgroundColor: theme.colors.successBg,
                    color: theme.colors.successText
                  } : {
                    backgroundColor: theme.colors.dangerBg,
                    color: theme.colors.dangerText
                  }}
                >
                  {rule.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {rules.length === 0 && (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 mx-auto mb-4" style={{ color: theme.colors.textTertiary }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.textPrimary }}>
            No rules configured
          </h3>
          <p className="mb-4" style={{ color: theme.colors.textSecondary }}>
            Create rules to define how your AI agents should handle obligations and escalations.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 rounded-lg"
            style={{
              backgroundColor: theme.colors.primaryColor,
              color: '#ffffff'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
          >
            Add Rule
          </button>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div 
            className="rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.colors.bgCard }}
          >
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4" style={{ color: theme.colors.textPrimary }}>
                {editingRule ? 'Edit Rule' : 'Add Rule'}
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Company
                    </label>
                    <select
                      value={formData.company_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.textPrimary
                      }}
                    >
                      <option value="">Select Company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>{company.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label 
                      className="block text-sm font-medium mb-1"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Rule Type
                    </label>
                    <select
                      value={formData.rule_type}
                      onChange={(e) => setFormData(prev => ({ ...prev, rule_type: e.target.value as any }))}
                      className="w-full px-3 py-2 rounded-lg focus:ring-2"
                      style={{
                        backgroundColor: theme.colors.bgPage,
                        border: `1px solid ${theme.colors.borderColor}`,
                        color: theme.colors.textPrimary
                      }}
                    >
                      <option value="obligation">Obligation</option>
                      <option value="escalation">Escalation</option>
                      <option value="objection_handling">Objection Handling</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Rule Name
                  </label>
                  <input
                    type="text"
                    value={formData.rule_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, rule_name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                    placeholder="Email Capture Rule"
                  />
                </div>

                <div>
                  <label 
                    className="block text-sm font-medium mb-1"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Priority (0-10, higher = more important)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 rounded-lg focus:ring-2"
                    style={{
                      backgroundColor: theme.colors.bgPage,
                      border: `1px solid ${theme.colors.borderColor}`,
                      color: theme.colors.textPrimary
                    }}
                  />
                </div>

                {/* Phases Configuration */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="block text-sm font-medium"
                      style={{ color: theme.colors.textSecondary }}
                    >
                      Phases
                    </label>
                    <button
                      onClick={addPhase}
                      className="text-sm"
                      style={{ color: theme.colors.primaryColor }}
                      onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.primaryHoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.primaryColor}
                    >
                      + Add Phase
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.configuration.phases.map((phase: any, index: number) => (
                      <div 
                        key={index} 
                        className="p-4 rounded-lg"
                        style={{ border: `1px solid ${theme.colors.borderColor}` }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span 
                            className="text-sm font-medium capitalize"
                            style={{ color: theme.colors.textPrimary }}
                          >
                            Phase {index + 1}: {phase.phase}
                          </span>
                          {formData.configuration.phases.length > 1 && (
                            <button
                              onClick={() => removePhase(index)}
                              className="text-sm"
                              style={{ color: theme.colors.dangerText }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label 
                              className="block text-xs font-medium mb-1"
                              style={{ color: theme.colors.textSecondary }}
                            >
                              Prompt
                            </label>
                            <input
                              type="text"
                              value={phase.prompt}
                              onChange={(e) => updatePhase(index, 'prompt', e.target.value)}
                              className="w-full px-3 py-1 rounded text-sm focus:ring-1"
                              style={{
                                backgroundColor: theme.colors.bgPage,
                                border: `1px solid ${theme.colors.borderColor}`,
                                color: theme.colors.textPrimary
                              }}
                              placeholder="What email should we use for confirmations?"
                            />
                          </div>

                          <div>
                            <label 
                              className="block text-xs font-medium mb-1"
                              style={{ color: theme.colors.textSecondary }}
                            >
                              Retries
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="5"
                              value={phase.retries}
                              onChange={(e) => updatePhase(index, 'retries', parseInt(e.target.value) || 0)}
                              className="w-full px-3 py-1 rounded text-sm focus:ring-1"
                              style={{
                                backgroundColor: theme.colors.bgPage,
                                border: `1px solid ${theme.colors.borderColor}`,
                                color: theme.colors.textPrimary
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Escalation Conditions */}
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    Escalate If
                  </label>
                  <div className="space-y-2">
                    {['no_response_after_retries', 'customer_frustration', 'hazard_mentioned', 'billing_dispute'].map(condition => (
                      <label key={condition} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.configuration.escalate_if?.includes(condition) || false}
                          onChange={(e) => {
                            const current = formData.configuration.escalate_if || [];
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  escalate_if: [...current, condition]
                                }
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                configuration: {
                                  ...prev.configuration,
                                  escalate_if: current.filter((c: string) => c !== condition)
                                }
                              }));
                            }
                          }}
                          className="rounded"
                          style={{
                            borderColor: theme.colors.borderColor,
                            color: theme.colors.primaryColor
                          }}
                        />
                        <span className="ml-2 text-sm capitalize" style={{ color: theme.colors.textPrimary }}>
                          {condition.replace('_', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingRule(null);
                    resetForm();
                  }}
                  className="px-4 py-2 rounded-lg"
                  style={{
                    color: theme.colors.textPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    backgroundColor: theme.colors.bgCard
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgCard}
                >
                  Cancel
                </button>
                <button
                  onClick={saveRule}
                  className="px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: theme.colors.primaryColor,
                    color: '#ffffff'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
                >
                  {editingRule ? 'Update' : 'Create'} Rule
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
